import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  AccessTime as PendingIcon,
  Done as DoneIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";

const APPROVAL_STATUS = {
  pending: { label: 'Pending', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  approved: { label: 'Approved', color: 'success', icon: <DoneIcon fontSize="small" /> },
  rejected: { label: 'Rejected', color: 'error', icon: <CloseIcon fontSize="small" /> }
};

const ApproverDashboard = ({ collapsed }) => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (user?.data?.[0]?.c_id && token) {
      fetchApprovals();
    }
  }, [user, token, page, rowsPerPage, statusFilter]);

  const fetchApprovals = async () => {
    if (!user?.data?.[0]?.c_id || !token) {
      console.log('Missing c_id or token');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const offset = page * rowsPerPage;
      
      console.log('Fetching approvals with params:', {
        approver_id: user.data[0].c_id,
        status: statusFilter || undefined,
        limit: rowsPerPage,
        offset: offset
      });

      const response = await axios.post(
        process.env.REACT_APP_CLIENT_LISTAPPROVALHANDLER, // Replace with actual endpoint
        {
          env: 'prod',
          approver_id: user.data[0].c_id,
          status: statusFilter || undefined,
          limit: rowsPerPage,
          offset: offset
        },
        { headers: { Authorization: token } }
      );

      console.log('Approvals list API response:', response.data);

      if (response.data?.data?.approvals) {
        setApprovals(response.data.data.approvals);
        setTotalCount(response.data.data.count || response.data.data.approvals.length);
      } else {
        setApprovals([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching approvals:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.data || err.message || 'Failed to load approvals');
      
      // Mock data for testing - remove this in production
      console.log('Using mock data for testing');
   
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (approval) => {
    setActionLoading(true);
    try {
      console.log('Approving task:', approval.id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_APPROVETASKHANDLER, // Replace with actual endpoint
        {
          env: 'prod',
          approval_id: approval.id,
          approver_id: user.data[0].c_id,
          agent_id: approval.agent_id,
          action: 'approve'
        },
        { headers: { Authorization: token } }
      );

      console.log('Task approved successfully:', response.data);
      
      // Update local state
      setApprovals(prev => prev.map(a => 
        a.id === approval.id 
          ? { ...a, status: 'approved', approved_at: new Date().toISOString() } 
          : a
      ));
      
      setDetailsOpen(false);
      setSelectedApproval(null);
      
    } catch (err) {
      console.error('Error approving task:', err);
      setError(err.response?.data?.data || 'Failed to approve task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (approval) => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      console.log('Rejecting task:', approval.id);
      
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_APPROVETASKHANDLER, // Replace with actual endpoint
        {
          env: 'prod',
          approval_id: approval.id,
          approver_id: user.data[0].c_id,
          agent_id: approval.agent_id,
          action: 'reject',
          reason: rejectReason
        },
        { headers: { Authorization: token } }
      );

      console.log('Task rejected successfully:', response.data);
      
      // Update local state
      setApprovals(prev => prev.map(a => 
        a.id === approval.id 
          ? { 
              ...a, 
              status: 'rejected', 
              rejected_at: new Date().toISOString(),
              rejection_reason: rejectReason 
            } 
          : a
      ));
      
      setDetailsOpen(false);
      setSelectedApproval(null);
      setRejectReason('');
      
    } catch (err) {
      console.error('Error rejecting task:', err);
      setError(err.response?.data?.data || 'Failed to reject task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (approval) => {
    setSelectedApproval(approval);
    setDetailsOpen(true);
    setRejectReason('');
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedApproval(null);
    setRejectReason('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const filters = ['pending', 'approved', 'rejected'];
    setStatusFilter(filters[newValue]);
    setPage(0);
  };

  const filteredApprovals = approvals.filter(approval =>
    approval.agent_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    approval.task_summary.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAgentTypeLabel = (type) => {
    const labels = {
      product_owner: 'Product Owner',
      scrum_master: 'Scrum Master',
      coder: 'Coder',
      tester: 'Tester',
      designer: 'Designer',
      devops: 'DevOps',
      analyst: 'Analyst'
    };
    return labels[type] || type;
  };

  const pendingCount = approvals.filter(a => a.status === 'pending').length;

  return (
    <Box sx={{ p: 3, width: `calc(100vw - ${collapsed ? '60px' : '240px'} - 48px)`, marginLeft: collapsed ? '60px' : '240px' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="600">
          Approvals Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and approve agent work that requires your approval
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Pending Approvals
              </Typography>
              <Typography variant="h4" fontWeight="600" color="warning.main">
                {approvals.filter(a => a.status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Approved Today
              </Typography>
              <Typography variant="h4" fontWeight="600" color="success.main">
                {approvals.filter(a => a.status === 'approved' && 
                  new Date(a.approved_at).toDateString() === new Date().toDateString()
                ).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Total Reviewed
              </Typography>
              <Typography variant="h4" fontWeight="600">
                {approvals.filter(a => a.status === 'approved' || a.status === 'rejected').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Status Filter */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            label={
              <Badge badgeContent={pendingCount} color="warning">
                Pending
              </Badge>
            } 
          />
          <Tab label="Approved" />
          <Tab label="Rejected" />
        </Tabs>
      </Paper>

      {/* Actions Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by agent name or task..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchApprovals} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Approvals Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Agent</strong></TableCell>
                <TableCell><strong>Task</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Triggered By</strong></TableCell>
                <TableCell><strong>Time</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading approvals...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredApprovals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No {statusFilter} approvals found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {statusFilter === 'pending' 
                        ? 'Agents will appear here when they complete work that needs your approval'
                        : `You haven't ${statusFilter} any agent work yet`
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredApprovals.map((approval) => (
                  <TableRow key={approval.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {approval.agent_name}
                        </Typography>
                        <Chip 
                          label={getAgentTypeLabel(approval.agent_type)}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {approval.task_type.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {approval.task_summary}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={APPROVAL_STATUS[approval.status]?.icon}
                        label={APPROVAL_STATUS[approval.status]?.label}
                        color={APPROVAL_STATUS[approval.status]?.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {approval.triggered_by}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(approval.created_at)}
                      </Typography>
                      {approval.status === 'approved' && (
                        <Typography variant="caption" color="success.main" display="block">
                          Approved: {formatDate(approval.approved_at)}
                        </Typography>
                      )}
                      {approval.status === 'rejected' && (
                        <Typography variant="caption" color="error.main" display="block">
                          Rejected: {formatDate(approval.rejected_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewDetails(approval)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {approval.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleApprove(approval)}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleViewDetails(approval)}
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!loading && filteredApprovals.length > 0 && (
          <>
            <Divider />
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Task Details</Typography>
            {selectedApproval && (
              <Chip
                icon={APPROVAL_STATUS[selectedApproval.status]?.icon}
                label={APPROVAL_STATUS[selectedApproval.status]?.label}
                color={APPROVAL_STATUS[selectedApproval.status]?.color}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {selectedApproval && (
            <Box>
              {/* Agent Info */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Agent Name</Typography>
                    <Typography variant="body1" fontWeight="500">{selectedApproval.agent_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Agent Type</Typography>
                    <Typography variant="body1" fontWeight="500">{getAgentTypeLabel(selectedApproval.agent_type)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Triggered By</Typography>
                    <Typography variant="body1">{selectedApproval.triggered_by}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Time</Typography>
                    <Typography variant="body1">{formatDate(selectedApproval.created_at)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Task Summary */}
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Task Summary
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedApproval.task_summary}
              </Typography>

              {/* Confidence Score */}
              {selectedApproval.metadata?.confidence_score && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Confidence Score
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" color="primary">
                      {(selectedApproval.metadata.confidence_score * 100).toFixed(0)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedApproval.metadata.reasoning}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Task Details */}
              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Detailed Information
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  wordWrap: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(selectedApproval.task_details, null, 2)}
                </pre>
              </Paper>

              {/* Rejection Reason (if rejected) */}
              {selectedApproval.status === 'rejected' && selectedApproval.rejection_reason && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Alert severity="error">
                    <Typography variant="subtitle2" gutterBottom>Rejection Reason:</Typography>
                    <Typography variant="body2">{selectedApproval.rejection_reason}</Typography>
                  </Alert>
                </>
              )}

              {/* Reject Reason Input (for pending items) */}
              {selectedApproval.status === 'pending' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <TextField
                    fullWidth
                    label="Rejection Reason (required to reject)"
                    placeholder="Provide a clear reason for rejection..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    multiline
                    rows={3}
                    disabled={actionLoading}
                  />
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDetails} disabled={actionLoading}>
            Close
          </Button>
          {selectedApproval?.status === 'pending' && (
            <>
              <Button
                onClick={() => handleReject(selectedApproval)}
                variant="outlined"
                color="error"
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : <RejectIcon />}
              >
                Reject
              </Button>
              <Button
                onClick={() => handleApprove(selectedApproval)}
                variant="contained"
                color="success"
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={20} /> : <ApproveIcon />}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApproverDashboard;